import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Input, Select, Tag, Typography, message } from 'antd';
import WatercolorLoader from '../components/WatercolorLoader';
import { getProviderSettings, updateProviderSettings } from '../illustration/api';

const { Title, Paragraph, Text } = Typography;

/** Display-only mask — never a real key; never sent to the server. */
const KEY_MASK = '••••••••••••••••••••';

export default function AdminPage() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  const [model, setModel] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [keyFocused, setKeyFocused] = useState(false);
  const [googleApiKey, setGoogleApiKey] = useState('');
  const [googleKeyFocused, setGoogleKeyFocused] = useState(false);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getProviderSettings();
      setSettings(data);
      setModel(data.model || data.defaultModel);
      // Never put the real key in state — only a bullet mask when configured.
      setApiKey('');
      setKeyFocused(false);
      setGoogleApiKey('');
      setGoogleKeyFocused(false);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const hasKey = Boolean(settings?.hasApiKey);
  const showMask = hasKey && !keyFocused && !apiKey;
  const fieldValue = showMask ? KEY_MASK : apiKey;
  const hasGoogleKey = Boolean(settings?.hasGoogleApiKey);
  const showGoogleMask = hasGoogleKey && !googleKeyFocused && !googleApiKey;
  const googleFieldValue = showGoogleMask ? KEY_MASK : googleApiKey;

  const onSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const body = { model, providerId: 'openai' };
      const typed = apiKey.trim();
      // Only send a newly typed key — never the bullet mask, never echo secrets.
      if (typed && typed !== KEY_MASK) {
        body.apiKey = typed;
      }
      const typedGoogle = googleApiKey.trim();
      if (typedGoogle && typedGoogle !== KEY_MASK && !/^•+$/.test(typedGoogle)) {
        body.googleApiKey = typedGoogle;
      }
      const next = await updateProviderSettings(body);
      setSettings(next);
      setModel(next.model);
      setApiKey('');
      setKeyFocused(false);
      setGoogleApiKey('');
      setGoogleKeyFocused(false);
      message.success('Provider settings saved on the illustration server');
    } catch (e) {
      setError(e.message);
      message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const onClearGoogleKey = async () => {
    setSaving(true);
    try {
      const next = await updateProviderSettings({ googleApiKey: '' });
      setSettings(next);
      setGoogleApiKey('');
      setGoogleKeyFocused(false);
      message.success('Google API key cleared from server secrets');
    } catch (e) {
      message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const onClearKey = async () => {
    setSaving(true);
    try {
      const next = await updateProviderSettings({ apiKey: '' });
      setSettings(next);
      setApiKey('');
      setKeyFocused(false);
      message.success('API key cleared from server secrets');
    } catch (e) {
      message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="workflow-shell__content watercolor-loader-slot watercolor-loader-slot--page">
        <WatercolorLoader size="large" />
      </div>
    );
  }

  return (
    <div className="workflow-shell__content" style={{ maxWidth: 640 }}>
      <Title
        level={1}
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 'var(--text-title)',
          letterSpacing: 'var(--tracking-title)',
          marginBottom: 4,
        }}
      >
        Admin
      </Title>
      <Paragraph type="secondary">
        Provider settings. API keys are stored only on the illustration server (
        <Text code>content/secrets/</Text> or env) and are never returned to the browser or written
        to Firestore.
      </Paragraph>

      {error && <Alert type="error" showIcon title={error} style={{ marginBottom: 16 }} />}

      <section
        style={{
          padding: 'var(--space-5)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          background: 'var(--color-bg-surface)',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <div>
          <Text strong style={{ display: 'block', marginBottom: 6 }}>
            Provider
          </Text>
          <Tag>{settings?.providerLabel || 'OpenAI Images'}</Tag>
          <Tag color={hasKey ? 'green' : 'orange'}>
            {hasKey ? 'API key configured' : 'API key missing'}
          </Tag>
          {hasKey && settings?.apiKeyLast4 && (
            <Tag>
              ends in {settings.apiKeyLast4} ({settings.apiKeySource})
            </Tag>
          )}
        </div>

        <div>
          <Text strong style={{ display: 'block', marginBottom: 6 }}>
            Image model
          </Text>
          <Select
            style={{ width: '100%', maxWidth: 420 }}
            value={model}
            onChange={setModel}
            options={(settings?.models || []).map((m) => ({
              value: m.id,
              label: m.label,
            }))}
          />
          <Text type="secondary" style={{ display: 'block', marginTop: 6, fontSize: 'var(--text-caption)' }}>
            Default toward GPT Image 2.5 Sunburst. Calls{' '}
            <Text code>openai.images.generate</Text> (current Images API).
          </Text>
        </div>

        <div>
          <Text strong style={{ display: 'block', marginBottom: 6 }}>
            OpenAI API key
          </Text>
          <Input.Password
            value={fieldValue}
            visibilityToggle={false}
            autoComplete="new-password"
            placeholder={hasKey ? undefined : 'sk-… — stored on server only'}
            onFocus={() => {
              setKeyFocused(true);
              if (showMask) setApiKey('');
            }}
            onBlur={() => {
              if (!apiKey.trim()) setKeyFocused(false);
            }}
            onChange={(e) => {
              const next = e.target.value;
              // Ignore paste/change that is only the display mask.
              if (next === KEY_MASK) return;
              setApiKey(next);
            }}
          />
          <Text type="secondary" style={{ display: 'block', marginTop: 6, fontSize: 'var(--text-caption)' }}>
            {hasKey
              ? 'Bullets mean a key is already saved (not the real secret). Focus the field to type a replacement; leave blank on Save to keep it.'
              : 'Empty field — no key configured yet.'}
          </Text>
        </div>

        <div>
          <Text strong style={{ display: 'block', marginBottom: 6 }}>
            Google API key
          </Text>
          <Input.Password
            value={googleFieldValue}
            visibilityToggle={false}
            autoComplete="new-password"
            placeholder={hasGoogleKey ? undefined : 'AQ.… — stored on server only'}
            onFocus={() => {
              setGoogleKeyFocused(true);
              if (showGoogleMask) setGoogleApiKey('');
            }}
            onBlur={() => {
              if (!googleApiKey.trim()) setGoogleKeyFocused(false);
            }}
            onChange={(e) => {
              const next = e.target.value;
              if (next === KEY_MASK || /^•+$/.test(next)) return;
              setGoogleApiKey(next);
            }}
          />
          <Text type="secondary" style={{ display: 'block', marginTop: 6, fontSize: 'var(--text-caption)' }}>
            {hasGoogleKey
              ? `Video model ${settings?.videoModel || 'gemini-omni-1.1-flash'}. Ends in ${settings?.googleApiKeyLast4}. Focus the field to replace it; leave blank on Save to keep it.`
              : 'For video. Empty field — no Google key configured yet.'}
          </Text>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button type="primary" loading={saving} onClick={onSave}>
            Save settings
          </Button>
          {hasKey && (
            <Button danger loading={saving} onClick={onClearKey}>
              Clear OpenAI key
            </Button>
          )}
          {hasGoogleKey && (
            <Button danger loading={saving} onClick={onClearGoogleKey}>
              Clear Google key
            </Button>
          )}
          <Button onClick={refresh}>Refresh status</Button>
        </div>

        <Paragraph type="secondary" style={{ marginBottom: 0, fontSize: 'var(--text-caption)' }}>
          Generate: <Text code>{settings?.endpoint}</Text>
          <br />
          With refs: <Text code>{settings?.editsEndpoint}</Text>
          <br />
          {settings?.note}
        </Paragraph>
      </section>
    </div>
  );
}
